/**
 * Документ «Закриття місяця» (ч.1031) — сторінка одного закриття.
 *
 * ч.1120 (рішення власника, «як у 1С»): документ ПРОВОДЯТЬ і РОЗПРОВОДЯТЬ.
 *  - «розпровести» прибирає все, що створило закриття: проводки закриття і
 *    документи кроків. Перед дією — перелік, що зникне і що залишиться.
 *  - «провести» на непроведеному документі закриває місяць знову.
 *  - «повторити невдалі кроки» — для закриття, де крок упав: виконані
 *    кроки-документи не чіпає, підсумки місяця перераховує.
 *  - «перепровести» — розпровести і одразу провести.
 * Раніше виконане закриття не видалялось і вдруге не запускалось: помилку в
 * документах місяця після закриття виправити було нічим.
 *
 * Закон дизайну #10: блоки ліворуч, рейка праворуч; дії — на нижній плашці.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { periodLock } from '@/api/closing';
import { monthCloseRuns, type MonthCloseRun } from '@/api/month_close_runs';
import { usePermissions } from '@/auth/usePermissions';
import ClosingSnapshotCard from '@/components/dashboard/ClosingSnapshotCard';
import DocBlock from '@/shared/components/DocBlock';
import DocLayout from '@/shared/components/DocLayout';
import DocRail from '@/shared/components/DocRail';
import DocStatusBadge from '@/shared/components/DocStatusBadge';
import DocumentToolbar, { type ExtraAction } from '@/shared/components/DocumentToolbar';
import { MONTHS_UK } from '@/shared/components/PeriodFilter';
import { READ_ONLY_HINT, useAccessMode } from '@/store/accessMode';

import {
  badge, fmtDay, formatOpSummary, OP_LABELS, stepDocumentLink, WARNING_LABELS,
} from './monthCloseLabels';
import { PERIOD_LOCK_PERMISSION } from './PeriodLockPanel';
import UnpostDialog, { UnpostItems } from './UnpostDialog';

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** Останній день місяця, YYYY-MM-DD. */
export function monthEnd(year: number, month: number): string {
  const last = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

export function MonthCloseRunPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const permissions = usePermissions();
  // ч.1119: роль «лише перегляд» — дії документа вимкнені (плашка — сама, тут — «Повʼязані дії»).
  const readOnly = useAccessMode((st) => st.readOnly);
  const [confirm, setConfirm] = useState<'unpost' | 'repost' | null>(null);

  const q = useQuery({
    queryKey: ['month-close-run', id],
    queryFn: () => monthCloseRuns.get(id!),
    enabled: !!id,
  });
  const d = q.data;

  // Заборона змін: якщо місяць під нею, закриття не провести і не розпровести.
  const lockQ = useQuery({
    queryKey: ['period-lock', d?.organization_id],
    queryFn: () => periodLock.get(d!.organization_id),
    enabled: !!d && permissions.has(PERIOD_LOCK_PERMISSION),
  });

  const applied = (run: MonthCloseRun) => {
    qc.setQueryData(['month-close-run', id], run);
    qc.invalidateQueries({ queryKey: ['month-close-runs'] });
    qc.invalidateQueries({ queryKey: ['period-lock'] });
    qc.invalidateQueries({ queryKey: ['doc-history'] });
    qc.invalidateQueries({ queryKey: ['dtkt-journal'] });
  };
  const post = useMutation({ mutationFn: () => monthCloseRuns.post(id!), onSuccess: applied });
  const retry = useMutation({ mutationFn: () => monthCloseRuns.retry(id!), onSuccess: applied });
  const unpost = useMutation({
    mutationFn: () => monthCloseRuns.unpost(id!),
    onSuccess: (run) => { applied(run); setConfirm(null); },
    onError: () => setConfirm(null),
  });
  const repost = useMutation({
    mutationFn: () => monthCloseRuns.repost(id!),
    onSuccess: (run) => { applied(run); setConfirm(null); },
    onError: () => { setConfirm(null); q.refetch(); },
  });
  const remove = useMutation({
    mutationFn: () => monthCloseRuns.remove(id!),
    onSuccess: () => navigate('/period-close/runs'),
  });

  if (q.isLoading) return <div className="card"><div className="sup__muted">Завантаження…</div></div>;
  if (!d) return <div className="card"><div className="sup__muted">Документ не знайдено.</div></div>;

  const period = `${MONTHS_UK[d.period_month] ?? d.period_month} ${d.period_year}`;
  const done = d.operations_results.filter((o) => o.status === 'SUCCESS').length;
  const failed = d.operations_results.filter((o) => o.status === 'FAILED').length;
  const skipped = d.operations_results.filter((o) => o.status === 'SKIPPED').length;
  const running = d.status === 'IN_PROGRESS';
  const busy = post.isPending || unpost.isPending || repost.isPending || retry.isPending;

  const lockDate = lockQ.data?.lock_date ?? null;
  const locked = !!lockDate && lockDate >= monthEnd(d.period_year, d.period_month);
  const lockedHint = locked
    ? `Місяць захищено від змін: дата заборони — ${fmtDay(lockDate)}. Пересуньте її на сторінці «Закриття місяця»`
    : undefined;

  const actions: ExtraAction[] = [
    {
      label: retry.isPending ? 'повторюємо…' : 'повторити невдалі кроки',
      onClick: () => retry.mutate(),
      disabled: !d.has_failed_steps || busy || locked || readOnly,
      title: readOnly ? READ_ONLY_HINT : lockedHint ?? (d.has_failed_steps
        ? 'Виконати знову кроки з помилкою і перерахувати підсумки місяця. Виконані кроки не повторюються'
        : 'Кроків з помилкою немає'),
    },
    {
      label: 'перепровести',
      onClick: () => setConfirm('repost'),
      disabled: !d.posted || busy || locked || readOnly,
      title: readOnly ? READ_ONLY_HINT : lockedHint ?? (d.posted
        ? 'Розпровести і одразу провести знову — закрити місяць за поточними документами'
        : 'Закриття не проведено — натисніть «провести»'),
    },
  ];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Закриття місяця {d.document_number} · {period}</h2>
        <DocumentToolbar
          onBackToList={() => navigate('/period-close/runs')}
          onSave={() => undefined}
          savable={false}
          postable
          isPosted={d.posted || running}
          onPost={() => post.mutate()}
          onUnpost={() => setConfirm('unpost')}
          postDisabled={busy || locked}
          postPending={post.isPending}
          unpostPending={busy}
          unpostDisabled={locked}
          unpostTitle={lockedHint
            ?? 'Прибрати все, що створило закриття: проводки закриття і документи кроків'}
          postTitle={lockedHint ?? 'Виконати перевірку і кроки закриття за цей місяць'}
          postLabel={post.isPending ? 'виконується…' : 'провести'}
          deletable
          onDelete={() => { if (window.confirm('Видалити цей документ закриття?')) remove.mutate(); }}
          deleteDisabled={running}
          deletePending={remove.isPending}
          deleteTitle={running ? 'Закриття ще виконується' : undefined}
          entriesDoc={{ type: 'doc.month_close_runs', id: d.id }}
          extraStatusBadges={[badge(d.status)]}
          hidePostedBadge
          railMode
        />
      </div>

      <DocLayout rail={
        <DocRail doc={{ type: 'doc.month_close_runs', id: d.id }} related={false} actions={actions} />
      }>
        <DocBlock index={1} title="Реквізити">
          <div className="mc__grid">
            <div><span className="mc__k">Період</span><span>{period}</span></div>
            <div><span className="mc__k">Статус</span><DocStatusBadge {...badge(d.status)} /></div>
            <div><span className="mc__k">Розпочато</span><span>{when(d.started_at)}</span></div>
            <div><span className="mc__k">Завершено</span><span>{when(d.completed_at)}</span></div>
            {d.comment && <div><span className="mc__k">Коментар</span><span>{d.comment}</span></div>}
          </div>
          {locked && (
            <div className="mc__panel mc__panel--info" data-testid="run-locked-note"
              style={{ marginTop: '0.6rem' }}>
              Місяць захищено від змін: дата заборони — {fmtDay(lockDate)}. Щоб провести або
              розпровести закриття, пересуньте її на сторінці{' '}
              <Link to="/period-close/runs">«Закриття місяця»</Link>.
            </div>
          )}
          {d.unpost_report && (
            <div className="mc__panel" data-testid="run-unpost-report" style={{ marginTop: '0.6rem' }}>
              <div className="mc__panel-head">Закриття розпроведено {when(d.unpost_report.at)}</div>
              {d.unpost_report.removed.length > 0 && (
                <>
                  <div className="mc__warn-title">Прибрано</div>
                  <UnpostItems items={d.unpost_report.removed} />
                </>
              )}
              {d.unpost_report.kept.length > 0 && (
                <>
                  <div className="mc__warn-title" style={{ marginTop: '0.4rem' }}>Залишено</div>
                  <UnpostItems items={d.unpost_report.kept} />
                </>
              )}
              {d.unpost_report.warnings.map((w, i) => (
                <div key={i} className="mc__warn-hint">{w}</div>
              ))}
              <div className="mc__warn-hint">
                Місяць відкритий для виправлень. Щоб закрити його знову, натисніть «провести».
              </div>
            </div>
          )}
        </DocBlock>

        <DocBlock index={2} title="Перевірка перед закриттям"
          hint="Що система перевірила до запуску операцій">
          <div className="mc__panel-head">
            <DocStatusBadge label={d.pre_check_passed ? 'пройдено' : 'заблоковано'}
              tone={d.pre_check_passed ? 'green' : 'red'} />
            {d.pre_check_warnings.length === 0 && <span>зауважень не було</span>}
            {!d.pre_check_passed && (
              <span>усуньте перелічене і натисніть «провести» — документ той самий</span>
            )}
          </div>
          {d.pre_check_warnings.map((w, i) => (
            <div key={i} className="mc__warn">
              <div className="mc__warn-head">
                <DocStatusBadge label={w.severity === 'INFO' ? 'до відома' : 'блокує'}
                  tone={w.severity === 'INFO' ? 'amber' : 'red'} />
                <span className="mc__warn-title">
                  {w.title || WARNING_LABELS[w.warning_type] || w.warning_type}: {w.count}
                </span>
              </div>
              {w.hint && <div className="mc__warn-hint">{w.hint}</div>}
              <ul className="mc__warn-list">
                {w.items.map((it: Record<string, unknown>, k) => (
                  <li key={k}>
                    {String(it.employee_name || it.external_id || it.employee_id || '')}
                    {it.document_number ? ` — ${String(it.document_number)}` : ''}
                    {it.vacation_start_date ? ` з ${String(it.vacation_start_date)}` : ''}
                    {it.start_date ? ` з ${String(it.start_date)}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </DocBlock>

        <DocBlock index={3} title="Операції закриття"
          hint={`виконано ${done} · пропущено ${skipped}${failed ? ` · з помилкою ${failed}` : ''}`}>
          {d.operations_results.length === 0 && (
            <div className="sup__muted">
              {d.unpost_report
                ? 'Закриття розпроведено — операцій у силі немає.'
                : 'Операції не запускались — закриття зупинилось на перевірці.'}
            </div>
          )}
          {failed > 0 && d.posted && (
            <div className="mc__panel mc__panel--warn" data-testid="run-failed-note">
              Частина кроків не виконалась. Усуньте причину, вказану біля кроку, і натисніть
              «повторити невдалі кроки» праворуч — виконані кроки не повторюються, підсумки
              місяця перераховуються.
            </div>
          )}
          {d.operations_results.map((op, i) => {
            const link = stepDocumentLink(op.operation_type, op.document_id);
            return (
              <div key={i} className="mc__op">
                <DocStatusBadge
                  label={op.status === 'SUCCESS' ? 'виконано' : op.status === 'SKIPPED' ? 'пропущено' : 'помилка'}
                  tone={op.status === 'SUCCESS' ? 'green' : op.status === 'SKIPPED' ? 'gray' : 'red'} />
                <span className="mc__op-name">{OP_LABELS[op.operation_type] ?? op.operation_type}</span>
                {link && <Link to={link.to}>{link.label}</Link>}
                {op.error_message && <span className="mc__op-err">{op.error_message}</span>}
                {op.summary && Object.keys(op.summary).length > 0 && (
                  <span className="mc__op-sum">{formatOpSummary(op.operation_type, op.summary)}</span>
                )}
              </div>
            );
          })}
        </DocBlock>

        <DocBlock index={4} title="Підсумок закриття">
          {d.closing_snapshot
            ? <ClosingSnapshotCard snapshot={d.closing_snapshot} />
            : <div className="sup__muted">Підсумку немає — закривних проводок у силі немає.</div>}
        </DocBlock>
      </DocLayout>

      {confirm && (
        <UnpostDialog runId={d.id} mode={confirm}
          pending={unpost.isPending || repost.isPending}
          onConfirm={() => (confirm === 'repost' ? repost.mutate() : unpost.mutate())}
          onClose={() => setConfirm(null)} />
      )}
    </div>
  );
}
